import { Container } from 'mouse-user-interface';
import Sidebar from '../../components/Sidebar/Sidebar';
import styles from './DocumentViewer.module.scss';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { docs } from '../../docs';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import { CodeBlock } from '../../components';

function DocumentViewer() {
    const [contentHeight, setContentHeight] = useState('100dvh');
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const { slug } = useParams();

    useEffect(() => {
        const updateHeight = () => {
            const header = document.getElementById('main-header');
            const dropdown = dropdownRef.current;

            let subtractedHeight = 0;

            if (header) {
                subtractedHeight += header.offsetHeight;
            }

            if (dropdown) {
                subtractedHeight += dropdown.offsetHeight;
            }

            subtractedHeight += 32;

            setContentHeight(`calc(100dvh - ${subtractedHeight}px)`);
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);

        const observer = new MutationObserver(updateHeight);
        const headerEl = document.getElementById('main-header');

        if (headerEl) {
            observer.observe(headerEl, { attributes: true, childList: true, subtree: true });
        }

        if (dropdownRef.current) {
            observer.observe(dropdownRef.current, { attributes: true, childList: true, subtree: true });
        }

        return () => {
            window.removeEventListener('resize', updateHeight);
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const scrollable = document.querySelector('body');
        if (scrollable) {
            scrollable.scrollTo({
                top: 0,
                left: 0,
                behavior: 'instant'
            });
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, [slug]);

    const currentDoc = useMemo(() => {
        if (!slug) return null;
        return docs.find(doc => {
            const docSlug = doc.id.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
            return docSlug === slug;
        });
    }, [slug]);

    const groupedDocs = useMemo(() => {
        return docs.reduce((acc, doc) => {
            if (!acc[doc.category]) acc[doc.category] = [];
            acc[doc.category].push(doc);
            return acc;
        }, {});
    }, []);

    const toSlug = (str) => str.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');

    const handleMobileNav = (e) => {
        const selectedSlug = e.target.value;
        if (selectedSlug) {
            navigate(`/docs/${selectedSlug}`);
        }
    };

    const MarkdownLoader = ({ path }) => {
        const [markdown, setMarkdown] = useState('');
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);

        useEffect(() => {
            if (!path) {
                setLoading(false);
                return;
            }

            fetch(path)
                .then((res) => {
                    if (!res.ok) throw new Error('Failed to load markdown');
                    return res.text();
                })
                .then((text) => {
                    setMarkdown(text);
                    setLoading(false);
                })
                .catch((err) => {
                    console.error(err);
                    setError('Could not load documentation content.');
                    setLoading(false);
                });
        }, [path]);

        if (loading) return <p>Loading documentation...</p>;
        if (error) return <p style={{ color: 'red' }}>{error}</p>;

        return (
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    pre({ children, ...props }) {
                        return (
                            <pre style={{ padding: 0 }} {...props}>
                                {children}
                            </pre>
                        );
                    },
                    code({ inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : 'text';
                        const codeString = String(children).replace(/\n$/, '');

                        if (inline) {
                            return <code className={className} {...props}>{children}</code>;
                        }

                        return <CodeBlock code={codeString} language={language} />;
                    },
                    table({ children }) {
                        return (
                            <div style={{ overflowX: 'auto' }}>
                                <table className={styles['document-viewer__content--table']}>
                                    {children}
                                </table>
                            </div>
                        );
                    }
                }}
            >
                {markdown}
            </ReactMarkdown>
        );
    };
    return (
        <Container className={styles['document-viewer']}>
            <Sidebar sidebarHeight={contentHeight} />
            <div
                ref={dropdownRef}
                className={`${styles['document-viewer__content--dropdown']} mobile-only`}
            >
                <select
                    name='docs-selection'
                    value={slug || ''}
                    onChange={handleMobileNav}
                    className={styles['document-viewer--select']}
                >
                    <option value='' disabled>
                        Select a section...
                    </option>

                    {Object.entries(groupedDocs).map(([category, items]) => (
                        <optgroup
                            key={category}
                            label={category.charAt(0).toUpperCase() + category.slice(1)}
                        >
                            {items.map((doc) => (
                                <option key={doc.id} value={toSlug(doc.id)}>
                                    {doc.title}
                                </option>
                            ))}
                        </optgroup>
                    ))}
                </select>
            </div>

            <Container
                scrollable
                className={`${styles['document-viewer__content']} hide-scrollbar`}
                style={{ height: contentHeight, scrollbarWidth: 'none', paddingTop: 0 }}
            >
                {currentDoc && (
                    <div className={styles['document-viewer__breadcrumb']}>
                        <span className={styles['document-viewer__breadcrumb--item']}>Documentation</span>
                        <span className={styles['document-viewer__breadcrumb--seperator']}>›</span>
                        <span className={styles['document-viewer__breadcrumb--item']}>
                            {currentDoc.category.charAt(0).toUpperCase() + currentDoc.category.slice(1)}
                        </span>
                        <span className={styles['document-viewer__breadcrumb--seperator']}>›</span>
                        <span className={styles['document-viewer__breadcrumb--item']}>
                            {currentDoc.title}
                        </span>
                    </div>
                )}

                <div className={styles.contentWrapper}>
                    {currentDoc ? (
                        <div className={styles.contentWrapper}>
                            <h1>{currentDoc.title}</h1>
                            <MarkdownLoader path={currentDoc.path} />
                        </div>
                    ) : (
                        <div className={styles.welcome}>
                            <h1>MouseUI Documentation</h1>
                            <p>Welcome to <strong>MouseUI</strong> - a lightweight, accessible, and highly customizable React component library for modern web applications.</p>
                            <p className={styles['intro-link']}>
                                Start with the <strong onClick={() => navigate('/docs/getting-started')}>Getting Started</strong> guide,
                                or select a component from the sidebar to view the documentation.
                            </p>
                        </div>
                    )}
                </div>
            </Container>
        </Container>
    );
}

export default DocumentViewer;